import React, { useCallback, useEffect, useState } from 'react';
import { Save } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ApiSubtag } from '../../../api/hexa/ApiSubtag';
import {
  SubtagDetailDialogProps,
  Subtag,
} from '../../../api/hexa/models/SubtagModels';

const DEFAULT_FORM: Omit<Subtag, 'id'> = {
  subtag: '',
  description: '',
  datasets: [],
};

const SubtagDetail = (props: SubtagDetailDialogProps) => {
  const [initialFormValue, setInitialFormValue] = useState(DEFAULT_FORM);
  const [isFormChanged, setIsFormChanged] = useState(true);
  const { open, clickedRow, onClose, subtagListUpdate } = props;
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const { handleSubmit, register, control, reset } = useForm<
    Omit<Subtag, 'id'>
  >({
    defaultValues: DEFAULT_FORM,
  });
  const formValues = useWatch({ control });

  /**
   * Check if the form values have changed, used to enable/disable save button
   */
  useEffect(() => {
    const isUnchanged =
      JSON.stringify(formValues) === JSON.stringify(initialFormValue);
    setIsFormChanged(isUnchanged);
  }, [formValues, initialFormValue]);

  /**
   * Reset the form values with the subtag data when the detail is opened for a specific row
   */
  useEffect(() => {
    const filledFormValues = {
      subtag: clickedRow?.subtag ?? '',
      description: clickedRow?.description ?? '',
      datasets: clickedRow?.datasets ?? [],
    };
    reset(filledFormValues);
    setInitialFormValue(filledFormValues);
  }, [clickedRow, reset]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  /**
   * Submit the form data to the API
   */
  const onSubmit = useCallback(async () => {
    setLoading(true);
    try {
      await ApiSubtag.update(clickedRow?.id ?? '', formValues);
      subtagListUpdate();
      handleClose();
    } catch (error) {
      alert(error);
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [clickedRow?.id, formValues, handleClose, subtagListUpdate]);

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      fullWidth
      maxWidth="md"
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(onSubmit),
      }}
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '0.8rem',
          padding: '1rem',
          paddingBottom: '2rem',
          maxHeight: 'calc(100% - 10.5rem)',
        },
      }}
    >
      <DialogTitle variant="h5" sx={{ paddingBottom: '0 !important' }}>
        {t('subtags_list.detail.title')}
        <DialogActions sx={{ padding: 0 }}>
          <LoadingButton
            loading={loading}
            type="submit"
            color="primary"
            variant="contained"
            disabled={loading || isFormChanged}
            startIcon={<Save />}
          >
            {t('save')}
          </LoadingButton>
        </DialogActions>
      </DialogTitle>
      <DialogContent sx={{ overflowY: 'unset' }}>
        <Stack
          sx={{
            display: 'grid',
            flexDirection: 'row',
            gap: '1rem',
          }}
        >
          <TextField
            sx={{ mb: 2, gridColumn: '1', gridRow: '1' }}
            label={t('subtags_list.detail.subtag')}
            {...register('subtag')}
            InputProps={{
              readOnly: true,
            }}
          />
          <TextField
            sx={{ mb: 2, gridColumn: '2', gridRow: '1' }}
            label={t('subtags_list.detail.description')}
            {...register('description')}
          />
          <Controller
            name="datasets"
            control={control}
            defaultValue={[]}
            render={({ field }) => (
              <Autocomplete
                multiple
                freeSolo
                options={[]} // or your dataset options if any
                value={field.value}
                onChange={(_, data) => field.onChange(data)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('subtags_list.detail.dataset')}
                    sx={{ mb: 2, gridColumn: '1', gridRow: '2' }}
                  />
                )}
              />
            )}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default SubtagDetail;
